import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
  Divider,
  InputBase,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useState, useMemo, useEffect } from "react";

export default function MemberSelectModal({ open, onClose, members, onSelect }) {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState([]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return members.filter(
      (m) =>
        m.idjemaat.toLowerCase().includes(s) ||
        m.name_full.toLowerCase().includes(s)
    );
  }, [search, members]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("recent-members") || "[]");
      setRecentIds(Array.isArray(saved) ? saved : []);
    } catch {
      setRecentIds([]);
    }
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [search, open]);

  const recentMembers = useMemo(() => {
    if (search.trim()) return [];
    const map = new Map(members.map((m) => [m.idjemaat, m]));
    return recentIds.map((id) => map.get(id)).filter(Boolean);
  }, [recentIds, members, search]);

  const combinedList = useMemo(() => {
    if (search.trim()) return filtered;
    const recentSet = new Set(recentMembers.map((m) => m.idjemaat));
    const rest = filtered.filter((m) => !recentSet.has(m.idjemaat));
    return [...recentMembers, ...rest];
  }, [filtered, recentMembers, search]);

  const handleSelect = (m) => {
    onSelect(m);
    onClose();
    const next = [m.idjemaat, ...recentIds.filter((id) => id !== m.idjemaat)].slice(0, 6);
    setRecentIds(next);
    localStorage.setItem("recent-members", JSON.stringify(next));
  };

  const handleKeyDown = (e) => {
    if (!combinedList.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % combinedList.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + combinedList.length) % combinedList.length);
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const m = combinedList[activeIndex];
      if (m) handleSelect(m);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          // background: "linear-gradient(135deg, rgba(226,232,240,0.5), rgba(248,250,252,0.9))",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={0.2}>
          <Typography variant="h6" fontWeight={800}>
            Pilih Member
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cari berdasarkan nama atau nomor anggota
          </Typography>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent  >
        {/* Search */}
        <Paper sx={{
          mt:1.5,
          mb: 1.5,
          p: 1.25,
          display: "flex",
          alignItems: "center",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "#f8fafc55",
          "&:focus-within": {
            borderColor: "#2563eb",
            boxShadow: "0 0 0 3px rgba(37,99,235,0.15)",
          },
        }}>
          <SearchIcon sx={{ opacity: 0.6, mr: 1 }} />

          <InputBase
            placeholder="Cari Anggota Betania..."
            fullWidth
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);

            }}
            onKeyDown={handleKeyDown}
            sx={{
              fontSize: "1rem",
              ml: 1,
            }}
          />

          <IconButton
            size="small"
            onClick={() => setSearch("")}
            sx={{ ml: 0.5 }}
            aria-label="Clear search"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Paper>
        <Divider sx={{ mb: 2 }} />
        {/* List */}
        <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
          <Stack spacing={1}>
            {recentMembers.length > 0 && !search.trim() && (
              <Box sx={{ px: 0.5, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Recently selected
                </Typography>
              </Box>
            )}
            {combinedList.map((m, i) => (
              <Box
                key={m.idjemaat}
                onClick={() => handleSelect(m)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  mr: 1,
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  transition: "all .15s ease",
                  backgroundColor: i === activeIndex ? "rgba(37,99,235,0.08)" : "transparent",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                    borderColor: "#cbd5e1",
                    boxShadow: "0 6px 16px rgba(15,23,42,0.12)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {m.name_full}
                    </Typography>
                    <Chip label={m.idjemaat} size="small" />
                    {m.phone_mobile && <Chip label={m.phone_mobile} size="small" variant="outlined" />}
                    {m.isbetaniamember !== undefined && (
                      <Chip
                        label={m.isbetaniamember === 1 ? "Member" : "Non Member"}
                        size="small"
                        color={m.isbetaniamember === 1 ? "success" : "default"}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {m.alamat}
                  </Typography>
                </Stack>
              </Box>
            ))}

            {combinedList.length === 0 && (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">
                  Member tidak ditemukan
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>

  );
}
